import { Label } from "@radix-ui/react-label";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import * as React from "react";
import { createContext, useContext } from "react";
import { ChevronIcon } from "../icons/chevron";
import { cn } from "../lib/utils";

type SelectContextType = {
	value: string;
	setValue: (value: string) => void;
	hasValue: boolean;
};

const SelectContext = createContext<SelectContextType | null>(null);

const useSelect = () => {
	const context = useContext(SelectContext);

	if (!context) {
		throw new Error("Select components must be used within a Select");
	}

	return context;
};

interface SelectProps
	extends Omit<SelectPrimitive.SelectProps, "value" | "onValueChange"> {
	value?: string;
	onChange?: SelectPrimitive.SelectProps["onValueChange"];
	defaultValue?: string;
}

const Select = ({
	children,
	value: controlledValue,
	onChange,
	defaultValue = "",
	...props
}: SelectProps) => {
	const [uncontrolledValue, setUncontrolledValue] =
		React.useState(defaultValue);

	const isControlled = controlledValue !== undefined;

	const value = isControlled ? controlledValue : uncontrolledValue;

	const handleValueChange = (newValue: string) => {
		if (!isControlled) {
			setUncontrolledValue(newValue);
		}

		onChange?.(newValue);
	};

	const hasValue = value !== undefined && value !== "";

	return (
		<SelectContext.Provider
			value={{ value, setValue: handleValueChange, hasValue }}
		>
			<SelectPrimitive.Root
				value={value}
				onValueChange={handleValueChange}
				defaultValue={defaultValue}
				{...props}
			>
				{children}
			</SelectPrimitive.Root>
		</SelectContext.Provider>
	);
};
Select.displayName = "Select";

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

type SelectTriggerProps = React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Trigger
> & {
	label?: string;
	rounded?: boolean;
	error?: string;
	helper?: string;
	containerClassName?: string;
};

const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	SelectTriggerProps
>(
	(
		{
			className,
			containerClassName,
			label,
			rounded = false,
			error,
			helper,
			...props
		},
		ref,
	) => {
		const { hasValue } = useSelect();
		const isError = !!error;
		const generatedId = React.useId();
		const id = props.id || generatedId;

		return (
			<div className={cn("w-full", containerClassName)}>
				<div className="relative isolate w-full">
					<SelectPrimitive.Trigger
						ref={ref}
						id={id}
						aria-invalid={isError}
						aria-describedby={
							isError ? `${id}-error` : helper ? `${id}-helper` : undefined
						}
						className={cn(
							// Layout
							"flex w-full",
							// Sizing & Dimensions
							"h-14 lg:h-16",
							// Typography
							"bb-text-body [font-size:min(1rem,16px)] [&_span]:truncate [&_span]:pr-7.5 [&_span]:lg:pr-10",
							// Colors
							"bg-white",
							// Borders
							"border-gray-border rounded-md border-2",
							// Spacing
							"pt-4 pr-3 pl-4 lg:pt-6.5 lg:pl-6",
							// States
							"focus:border-gray-2 focus:outline-none",
							"disabled:bg-gray-1 disabled:cursor-not-allowed",
							// Transitions
							"transition-all",
							// Conditional styles
							{
								"pt-5 pl-4": hasValue,
								"rounded-4xl pl-5": rounded,
								"border-error focus:border-error": isError,
							},
							className,
						)}
						{...props}
					>
						<SelectPrimitive.Value className="w-full truncate" />
						<SelectPrimitive.Icon asChild>
							<ChevronIcon
								className={cn(
									// Layout & Positioning
									"pointer-events-none absolute top-1/2 z-30 -translate-y-1/2",
									// Colors
									"text-gray-2",
									// Sizing
									"h-6 w-6",
									// Conditional positioning
									{
										"right-4.5 lg:right-6.5": !rounded,
										"right-6.5 lg:right-6.5": rounded,
									},
								)}
							/>
						</SelectPrimitive.Icon>
					</SelectPrimitive.Trigger>

					{label && (
						<Label
							htmlFor={id}
							className={cn(
								// Typography overrides
								"![font-family:unset] !leading-6 ![font-weight:unset] !normal-case",
								// Layout & Positioning
								"pointer-events-none absolute z-10 max-w-[calc(100%-4rem)] truncate overflow-hidden",
								"lg:max-w-[calc(100%-5.5rem)]",
								// Colors
								"text-gray-2",
								// Horizontal positioning
								"left-4.5 lg:left-6.5",
								// Animation
								"transform-gpu transition-all ease-in-out",
								// Conditional styles
								{
									"left-6.5": rounded,
									"!bb-text-body -translate-y-10 ![font-size:min(1rem,16px)] lg:-translate-y-11":
										!hasValue,
									"!bb-text-tiny -translate-y-12.5 lg:-translate-y-14":
										hasValue,
								},
							)}
						>
							{label}
						</Label>
					)}
				</div>

				{isError && (
					<div
						id={`${id}-error`}
						className="bb-text-tiny text-error mt-1 ml-0.5"
						role="alert"
					>
						{error}
					</div>
				)}

				{helper && !isError && (
					<div
						id={`${id}-helper`}
						className="bb-text-tiny text-gray-2 mt-1 ml-0.5"
					>
						{helper}
					</div>
				)}
			</div>
		);
	},
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollUpButton
		ref={ref}
		className={cn(
			"flex cursor-default items-center justify-center py-1",
			className,
		)}
		{...props}
	>
		<ChevronIcon className="h-4 w-4 rotate-180" />
	</SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollDownButton
		ref={ref}
		className={cn(
			"flex cursor-default items-center justify-center py-1",
			className,
		)}
		{...props}
	>
		<ChevronIcon className="h-4 w-4" />
	</SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
	SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
			ref={ref}
			className={cn(
				// Layout & Positioning
				"relative z-50 overflow-x-hidden overflow-y-auto",
				// Sizing
				"min-w-[8rem]",
				// Appearance
				"text-gray-4 border-gray-border rounded-md border-2 bg-white shadow-md",
				// Origin & Transforms
				"origin-[--radix-select-content-transform-origin]",
				// Translations
				"data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1",
				"data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
				// Animations
				"data-[state=open]:animate-in data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
				className,
			)}
			style={{
				maxHeight: "var(--radix-select-content-available-height)",
				width: "var(--radix-select-trigger-width)",
			}}
			position="popper"
			sideOffset={5}
			{...props}
		>
			<SelectScrollUpButton />
			<SelectPrimitive.Viewport
				className={cn("w-full p-1")}
				style={{
					height: "var(--radix-select-trigger-height)",
					minWidth: "var(--radix-select-trigger-width)",
				}}
			>
				{children}
			</SelectPrimitive.Viewport>
			<SelectScrollDownButton />
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={cn(
			// Typography
			"text-sm font-semibold",
			// Spacing
			"px-2 py-1.5",

			className,
		)}
		{...props}
	/>
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={cn(
			// Layout & Positioning
			"relative flex items-center",
			"select-none",
			// Sizing & Dimensions
			"w-full",
			// Typography
			"bb-text-tiny",
			// Spacing
			"py-2 pr-8 pl-2 lg:pl-5",
			// Borders & Outlines
			"rounded-sm outline-none",
			// Interactivity
			"cursor-default",
			// States
			"data-[highlighted]:bg-gray-1",
			"data-[disabled]:pointer-events-none data-[disabled]:opacity-50",

			className,
		)}
		{...props}
	>
		<span className="absolute right-4 flex h-3.5 w-3.5 items-center justify-center">
			<SelectPrimitive.ItemIndicator>
				<Check className="h-4 w-4" strokeWidth={1.5} />
			</SelectPrimitive.ItemIndicator>
		</span>
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={cn(
			// Sizing & Dimensions
			"h-px",
			// Spacing
			"-mx-1 my-1",
			// Colors & Background
			"bg-gray-4",
			className,
		)}
		{...props}
	/>
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
};
